// src/stats/Sparkline.tsx

interface SparklineProps {
  points: { date: string; value: number }[]
  width?: number
  height?: number
  color?: string
}

/**
 * Mini graphe en courbe SVG.
 * Utilisé dans KpiCard comme contexte visuel (pas d'axes, pas de labels).
 */
export function Sparkline({
  points,
  width = 80,
  height = 32,
  color = 'var(--arancio)',
}: SparklineProps) {
  if (points.length < 2) return null

  const values = points.map((p) => p.value)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min === 0 ? 1 : max - min

  const pad = 2
  const w = width - pad * 2
  const h = height - pad * 2

  const toX = (i: number) => pad + (i / (points.length - 1)) * w
  const toY = (v: number) => pad + h - ((v - min) / range) * h

  const d = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(p.value).toFixed(1)}`)
    .join(' ')

  // Zone de remplissage sous la courbe
  const fillD =
    d +
    ` L${toX(points.length - 1).toFixed(1)},${(pad + h).toFixed(1)}` +
    ` L${toX(0).toFixed(1)},${(pad + h).toFixed(1)} Z`

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
      style={{ display: 'block', overflow: 'visible' }}
    >
      <defs>
        <linearGradient id={`spark-grad-${color}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.25} />
          <stop offset="100%" stopColor={color} stopOpacity={0.02} />
        </linearGradient>
      </defs>
      <path d={fillD} fill={`url(#spark-grad-${color})`} stroke="none" />
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
